import { getMsal } from '../auth/msal.js'

/**
 * Microsoft Graph Client fuer FlawDesk-for-Outlook (#1088).
 *
 * Aufgabe: zu einer offenen Mail im Outlook-Taskpane den gesamten Konversations-
 * Thread laden. Wir nutzen dafuer Graph-API mit dem `Mail.Read`-Scope, weil
 * Office.js selbst keinen Weg bietet, an die anderen Mails der gleichen
 * conversationId zu kommen.
 *
 * WICHTIG — Mail.Read-Token-Acquire passiert STRIKT SILENT:
 *   - Kein acquireTokenPopup-Fallback, kein Popup im NAA-iframe.
 *   - Das wuerde MSAL in "interaction_in_progress" sperren und nachfolgende
 *     api.flawdesk.de-Tokens stillschweigend blockieren.
 *   - Wenn silent failt (Consent fehlt, Token-Cache leer): graceful raisen
 *     und das Ticket ohne Thread erstellen lassen. NIE interactive werden.
 *   - Tenant-Admin muss Admin-Consent fuer Mail.Read erteilt haben, damit
 *     der silent acquire ohne User-Interaction durchgeht.
 */

const GRAPH_BASE = 'https://graph.microsoft.com/v1.0'
const SCOPES = ['https://graph.microsoft.com/Mail.Read']

/**
 * Wie acquireToken in auth/msal.js, aber STRIKT silent. Kein Popup-Fallback.
 * Sicherstellt dass MSAL nicht in interaction_in_progress laufen kann,
 * was nachfolgende FlawDesk-API-Calls blockieren wuerde.
 */
async function acquireGraphTokenSilent() {
  const pca = await getMsal()
  const accounts = pca.getAllAccounts()
  if (accounts.length === 0) {
    throw new Error('No MSAL account — login first')
  }
  if (!pca.getActiveAccount()) {
    pca.setActiveAccount(accounts[0])
  }
  // Bewusst NUR acquireTokenSilent. Kein Popup-Fallback — siehe Modul-Header.
  return await pca.acquireTokenSilent({ scopes: SCOPES })
}

/**
 * Liefert das Graph-Mail-Objekt fuer alle Mails der gegebenen conversationId,
 * sortiert chronologisch (aelteste zuerst). Mailt zurueck als Array von
 * gemappten Mail-Records im Format das das FlawDesk-Backend erwartet.
 *
 * Default-Top: 200 — das deckt 99.9% aller real-life Threads ab. Selbst
 * hartgesottene Mail-Faden mit 50-100 Replies kommen damit klar.
 *
 * Bei Fehlern wirft die Funktion — der Caller sollte fangen und entscheiden
 * ob er den Task auch ohne Thread anlegt (graceful degradation) oder den
 * User informiert.
 */
export async function fetchConversationThread(conversationId, { top = 200 } = {}) {
  if (!conversationId) {
    throw new Error('fetchConversationThread: conversationId required')
  }

  const tokenResult = await acquireGraphTokenSilent()
  if (!tokenResult?.accessToken) {
    throw new Error('Mail.Read token not available')
  }

  // Graph-Filter-Quirk: die Kombination $filter=conversationId + $orderby
  // wirft "InefficientFilter" (400), obwohl die Doku das so vorschlaegt.
  // Workaround: nur $filter, KEIN $orderby — wir sortieren clientseitig.
  // Auch $select lassen wir weg, das hilft Graph's Query-Planner zusaetzlich.
  // Sortierung passiert nach dem Map nach sentDateTime asc.
  //
  // conversationId enthaelt typisch Base64-Zeichen (=, /, +) die URL-encoded
  // werden muessen. URLSearchParams.set() macht das fuer den Value-Teil
  // automatisch — aber wir konstruieren den Filter-String SELBST, deshalb
  // achten wir auf doppelte Single-Quotes (OData-Escaping).
  const escapedId = conversationId.replace(/'/g, "''")
  const url = new URL(`${GRAPH_BASE}/me/messages`)
  url.searchParams.set('$filter', `conversationId eq '${escapedId}'`)
  url.searchParams.set('$top', String(top))

  // Body ist standardmaessig HTML. Wir wollen Plaintext, dafuer gibt es den
  // ConsistencyLevel-Header und Prefer-Header:
  const resp = await fetch(url.toString(), {
    headers: {
      Authorization: `Bearer ${tokenResult.accessToken}`,
      Prefer: 'outlook.body-content-type="text"',
    },
  })

  if (!resp.ok) {
    const text = await resp.text().catch(() => '')
    throw new Error(`Graph fetch failed: ${resp.status} ${resp.statusText} — ${text.slice(0, 200)}`)
  }

  const data = await resp.json()
  const messages = Array.isArray(data?.value) ? data.value : []

  // Clientseitige Sortierung — Graph $orderby zusammen mit $filter auf
  // conversationId schlaegt mit InefficientFilter fehl, deshalb sortieren
  // wir hier nach Mapping. sentDateTime ist ein ISO-String, lexikographische
  // Sortierung entspricht chronologischer.
  const mapped = messages.map(mapGraphMessageToBackendFormat)
  mapped.sort((a, b) => {
    if (!a.sent_at) return 1
    if (!b.sent_at) return -1
    return a.sent_at < b.sent_at ? -1 : a.sent_at > b.sent_at ? 1 : 0
  })
  return mapped
}

/**
 * Mappt eine Graph /me/messages-Resource in das Format, das das FlawDesk-
 * Backend (POST /api/tasks mit mail_thread) erwartet.
 *
 * Wichtige Mappings:
 *   - internet_message_id: Graph nennt das internetMessageId (camelCase ohne
 *     Hyphen). RFC 5322 Message-ID, eindeutig.
 *   - from_email / from_name: Graph hat from.emailAddress.{address,name}.
 *     Beide Felder koennen fehlen, deshalb defensive Defaults.
 *   - in_reply_to: Graph liefert das nicht als Top-Level Feld, sondern als
 *     "In-Reply-To"-Header in internetMessageHeaders. Wir extrahieren das
 *     manuell, damit wir spaeter (Phase 2, #1092) Tree-Render bauen koennen.
 *   - body_text: Mit Prefer-Header bekommen wir hier schon Plaintext. body.contentType
 *     sollte 'text' sein; falls Graph dem Prefer nicht folgt und 'html'
 *     liefert (passiert bei aelteren Mailbox-Servern), strippen wir die
 *     HTML-Tags grob — besser als gar nichts.
 */
function mapGraphMessageToBackendFormat(msg) {
  const from = msg.from?.emailAddress ?? {}
  const inReplyTo = extractHeader(msg.internetMessageHeaders, 'In-Reply-To')

  let bodyText = msg.body?.content ?? ''
  if (msg.body?.contentType === 'html') {
    // Sehr defensiver HTML-Strip — keinen DOMParser hier nutzen, weil das
    // im Office-iframe nicht zuverlaessig laeuft. Wir wollen kein perfektes
    // HTML→Text, nur etwas Lesbares fuer den Notfall.
    bodyText = bodyText
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n\n')
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
  }

  bodyText = cleanMailBody(bodyText)

  return {
    internet_message_id: msg.internetMessageId,
    in_reply_to: inReplyTo,
    conversation_id: msg.conversationId,
    from_name: from.name || null,
    from_email: from.address || '',
    to_addrs: mapRecipients(msg.toRecipients),
    cc_addrs: mapRecipients(msg.ccRecipients),
    subject: msg.subject || '',
    body_text: bodyText,
    sent_at: toMySQLDatetime(msg.sentDateTime),
    received_at: toMySQLDatetime(msg.receivedDateTime),
  }
}

/**
 * Universelle Plaintext-Normalisierung fuer Mail-Bodies (#1088).
 *
 * Ziel: rohen Plaintext aus beliebigen Mail-Clients (Outlook, Gmail,
 * Thunderbird, mobile Clients) in eine Form bringen, die der bestehende
 * FlawDesk-RichText-Renderer sauber als Absaetze rendert. Wir aendern NUR
 * was strukturell sicher und Provider-unabhaengig ist:
 *
 *  1. CID-Inline-Image-Marker [cid:...] entfernen
 *     Outlook/MIME-Artefakt fuer eingebettete Signatur-Logos. Universell,
 *     da von jedem Mail-Client der HTML-Mails schreibt produziert.
 *
 *  2. Markdown-Link-Syntax aus Outlook-Plaintext entfernen
 *     Outlook Plaintext-Export schreibt URLs als "label<url>" — z.B.
 *     "www.inmet.de<http://www.inmet.de/>" oder
 *     "juergen.drossel@inmet.de<mailto:juergen.drossel@inmet.de>". Das
 *     ist NICHT Standard, aber ein bekanntes Outlook-Format. Wir entfernen
 *     den "<...>"-Teil, weil das Label davor die lesbare Variante ist.
 *
 *  3. Trailing-Whitespace pro Zeile abschneiden
 *     RFC 3676 format=flowed nutzt Trailing-Spaces als Wrap-Marker, aber
 *     im Plaintext den wir bekommen ist das nicht zuverlaessig. Trim wirft
 *     keine Information weg.
 *
 *  4. Mehrere Leerzeilen (>= 3 \n) auf zwei normalisieren
 *     Mehrere Leerzeilen hintereinander entstehen oft durch Quote-Header
 *     oder fehlerhafte Mail-Generierung. Zwei \n reichen fuer Absatz-Trennung.
 *
 *  5. Fuehrende und nachfolgende Leerzeilen entfernen
 *     Outlook-Mails haben oft "\n\nGesendet von Outlook fuer Android\n"
 *     am Ende. Die behalten wir als Absatz — entfernen aber nur das outer
 *     Whitespace, damit der Renderer nicht mit einem leeren ersten Absatz
 *     startet.
 *
 * Was wir explizit NICHT tun:
 *   - Signaturen abtrennen (jede Signatur sieht anders aus)
 *   - Quoted Text einklappen oder entfernen (gewollt im Thread-Kontext)
 *   - Hard-Wraps in Saetzen aufloesen (zu unsicher: Absicht vs RFC-Wrap)
 *   - Inhalts-spezifische Regex (kein "wenn Inmet" oder "wenn Outlook")
 */
function cleanMailBody(text) {
  if (!text) return ''
  let s = text
  // CID-Image-Marker entfernen
  s = s.replace(/\[cid:[^\]]+\]/g, '')
  // Outlook-Plaintext "label<url>" / "addr<mailto:addr>" — Tag entfernen
  // (Label bleibt). Wir matchen nur < ... > die wie eine URL/Mailto aussehen,
  // damit echter Text mit "<...>" (z.B. "siehe <Anhang>") nicht zerstoert wird.
  s = s.replace(/<(?:https?:\/\/|mailto:)[^>]+>/g, '')
  // Trailing-Whitespace pro Zeile
  s = s.split('\n').map((l) => l.replace(/\s+$/, '')).join('\n')
  // 3+ Leerzeilen auf 2 normalisieren
  s = s.replace(/\n{3,}/g, '\n\n')
  // Outer Whitespace trimmen
  return s.trim()
}

function mapRecipients(list) {
  if (!Array.isArray(list)) return []
  return list
    .map((r) => ({
      name: r.emailAddress?.name || null,
      email: r.emailAddress?.address || '',
    }))
    .filter((r) => r.email)
}

function extractHeader(headers, name) {
  if (!Array.isArray(headers)) return null
  const found = headers.find((h) => h?.name?.toLowerCase() === name.toLowerCase())
  return found?.value || null
}

/**
 * Graph liefert ISO-8601 mit Z (UTC) — z.B. "2026-05-21T08:15:30Z".
 * MySQL DATETIME erwartet "YYYY-MM-DD HH:MM:SS" ohne TZ-Suffix.
 * Wir speichern in UTC und konvertieren zur Anzeige im Frontend.
 */
function toMySQLDatetime(iso) {
  if (!iso) return null
  // "2026-05-21T08:15:30Z" → "2026-05-21 08:15:30"
  return iso.replace('T', ' ').replace(/\.\d+Z?$/, '').replace(/Z$/, '')
}
